"use client";

import { useEffect, useRef } from "react";
import { APP_VERSION } from "../lib/version";

// Silent force-upgrade for cached / installed (home-screen, desktop PWA) clients.
//
// Each build bakes in APP_VERSION. This polls a no-store /api/version (served by
// whatever deployment is currently live) on load, tab-focus, visibility, and a
// 2-minute interval. When the live release is newer than the one this client is
// running, it clears caches + updates the service worker and hard-reloads onto the
// new build — no prompt. A per-target sessionStorage flag prevents reload loops.
//
// `holdOnPaths` lets a route defer the refresh while a destructive reload would
// lose work in progress (e.g. the customer checkout / payment page) — the upgrade
// just happens on the next check once the user has navigated away.
export function VersionGuard({
  endpoint = "/api/version",
  holdOnPaths = [],
  intervalMs = 120_000,
}: {
  endpoint?: string;
  holdOnPaths?: string[];
  intervalMs?: number;
}) {
  const busy = useRef(false);

  useEffect(() => {
    let cancelled = false;

    async function upgrade(live: number) {
      // Don't yank the page out from under work in progress.
      if (holdOnPaths.some((p) => location.pathname.startsWith(p))) return;
      // Attempt a given target version at most once per tab session → no loops.
      const key = `aluume-upgrade:${live}`;
      if (sessionStorage.getItem(key)) return;
      sessionStorage.setItem(key, "1");
      try {
        if ("serviceWorker" in navigator) {
          const regs = await navigator.serviceWorker.getRegistrations();
          await Promise.all(regs.map((r) => r.update().catch(() => {})));
        }
        if (typeof caches !== "undefined") {
          const keys = await caches.keys();
          await Promise.all(keys.map((k) => caches.delete(k)));
        }
      } catch {
        // best effort — reload regardless
      }
      location.reload();
    }

    async function check() {
      if (cancelled || busy.current || !navigator.onLine) return;
      busy.current = true;
      try {
        const res = await fetch(endpoint, { cache: "no-store" });
        if (!res.ok) return;
        const live = Number((await res.json())?.version);
        if (Number.isFinite(live) && live > APP_VERSION) await upgrade(live);
      } catch {
        // offline / transient — ignore, try again next tick
      } finally {
        busy.current = false;
      }
    }

    const onVisible = () => {
      if (document.visibilityState === "visible") check();
    };

    check();
    const timer = setInterval(check, intervalMs);
    window.addEventListener("focus", check);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      cancelled = true;
      clearInterval(timer);
      window.removeEventListener("focus", check);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [endpoint, intervalMs, holdOnPaths]);

  return null;
}
